import { Setlist } from '@/graphql';
import { differenceInMinutes, format } from 'date-fns';

export enum SetlistStatusEnum {
	EDITING = 'Editing',
	DRAFT = 'Draft',
	UPCOMING = 'Upcoming',
	LIVE = 'Live',
	IN_REVIEW = 'In Review',
	COMPLETE = 'Complete',
}

type GetSetlistStatusReturn = {
	status: SetlistStatusEnum;
	colorSchemeClassName: string;
};

export const getSetlistStatus = (
	setlist?: Pick<Setlist, 'startAt' | 'draft'>,
	isEdit?: boolean,
): GetSetlistStatusReturn => {
	if (isEdit || !setlist) {
		return {
			status: SetlistStatusEnum.EDITING,
			colorSchemeClassName: 'px-color-scheme-grey',
		};
	}

	const currentTime = new Date();
	const startAtDate = new Date(setlist.startAt);

	if (setlist.draft) {
		return {
			status: SetlistStatusEnum.DRAFT,
			colorSchemeClassName: 'px-color-scheme-grey',
		};
	}

	if (startAtDate > currentTime) {
		return {
			status: SetlistStatusEnum.UPCOMING,
			colorSchemeClassName: 'px-color-scheme-green',
		};
	}

	if (
		differenceInMinutes(currentTime, startAtDate) > 0 &&
		differenceInMinutes(currentTime, startAtDate) < 60
	) {
		return {
			status: SetlistStatusEnum.LIVE,
			colorSchemeClassName: 'px-color-scheme-red',
		};
	}

	// CLIENT-SIDE STUB: show "Give Feedback" for 24 hours after startAt.
	// Replace with `const inReview = setlist.inReview ?? false` once Ifko ships
	// `inReview: Boolean` on the Setlist GQL type (ListMySetlists + GetSetlist).
	const inReview = differenceInMinutes(currentTime, startAtDate) < 1440;

	if (inReview) {
		return { status: SetlistStatusEnum.IN_REVIEW, colorSchemeClassName: 'px-color-scheme-blue' };
	}

	return {
		status: SetlistStatusEnum.COMPLETE,
		colorSchemeClassName: 'px-color-scheme-grey',
	};
};

export type FeedbackEmptyStateConfig = {
	heading: string;
	body: string;
	ctaLabel?: string;
	ctaSetlistId?: string;
} | null;

export const getFeedbackEmptyStateConfig = (
	songSetlists: Array<Pick<Setlist, 'id' | 'startAt' | 'draft' | 'serviceId'>>,
	isLeader: boolean,
	services: Array<{ id: string; name: string }> = [],
	now: Date = new Date(),
): FeedbackEmptyStateConfig => {
	// FBTAB-01: Song has never been in a setlist
	if (songSetlists.length === 0) {
		return {
			heading: 'Give feedback after a service',
			body: 'Add it to a setlist then rate it after. Your first reaction is the most honest one.',
		};
	}

	// Sort by startAt descending; take most recent
	const sorted = [...songSetlists].sort(
		(a, b) => new Date(b.startAt).getTime() - new Date(a.startAt).getTime(),
	);
	const mostRecent = sorted[0];
	const { status } = getSetlistStatus(mostRecent);

	// FBTAB-02: Most recent setlist is upcoming or live
	if (status === SetlistStatusEnum.UPCOMING || status === SetlistStatusEnum.LIVE) {
		const service = services.find((s) => s.id === mostRecent.serviceId);
		const serviceName = service?.name || '';
		const serviceTime = mostRecent.startAt
			? format(new Date(mostRecent.startAt), 'h:mm a')
			: '';
		const heading =
			serviceName && serviceTime
				? `Rate this after ${serviceName} at ${serviceTime}`
				: 'Rate this after your upcoming service';
		return {
			heading,
			body: 'Your most honest reaction is right after the service.',
		};
	}

	const inReview = false; // stub: replace with setlist.inReview when server ships field

	// FBTAB-03 / FBTAB-06: Setlist is in review
	if (inReview) {
		const dayName = mostRecent.startAt
			? format(new Date(mostRecent.startAt), 'EEEE')
			: 'the service';

		if (isLeader) {
			return {
				heading: 'Now\'s the moment',
				body: `How did this one land today? Rate it while ${dayName} is still with you.`,
				ctaLabel: 'Rate this setlist →',
				ctaSetlistId: mostRecent.id,
			};
		} else {
			return {
				heading: 'How did this one land?',
				body: `After ${dayName}'s service, take a minute to share how it went. Your leader will see it.`,
				ctaLabel: 'Rate this setlist →',
				ctaSetlistId: mostRecent.id,
			};
		}
	}

	const windowExpired = false; // stub: server field not yet available

	// FBTAB-04: Window expired but setlist not yet complete
	if (windowExpired && status !== SetlistStatusEnum.COMPLETE) {
		return {
			heading: 'Still worth capturing',
			body: 'You\'re past the ideal window, but it\'s still worth it. Rate before the day\'s out.',
			ctaLabel: 'Rate this setlist →',
			ctaSetlistId: mostRecent.id,
		};
	}

	// FBTAB-05: Most recent setlist is complete
	if (status === SetlistStatusEnum.COMPLETE) {
		return {
			heading: 'Catch the next one',
			body: 'Feedback is most useful right after the service. Next time this song comes up, rate it within the hour.',
		};
	}

	// No empty state — existing feedback data; show charts only
	return null;
};
